import React, { useState, useCallback } from 'react';
import {
  Button,
  InfoMessage,
  MenuTab,
  Modal,
  ModalInner,
  Text
} from 'components';

import { RegisterSpaceCraft } from './types';
import {
  registerPolarizationList,
  registerModulationList,
  reedSolomonCodeList,
  registerPCMCodingList,
  registerConvolutionalCodeList,
  registerSpaceCraftList,
  registerUplinkPCMCodeList,
  registerOperatingModeList
} from './utils';
import axios from 'common/AxiosAuth';
import { Message } from 'types/message';
import { InformationCircleOutlineIcon } from 'assets/svgs';
import MenuButton from 'components/MenuButton';
import SDownLink from './SDownLink';
import SUpLink from './SUpLink';
import XDownLink from './XDownLink';
import { useSpaceCraftForm } from 'hooks/useSpaceCraftForm';
import { useTLEData } from 'hooks/useTLEData';
import { FormData, validateForm, validateField } from 'utils/validation';
import { useTranslation } from 'react-i18next';

interface ModalProps { 
  isOpened: boolean;
  setIsOpened: React.Dispatch<React.SetStateAction<boolean>>;
  onSpaceCraftUpdated: () => Promise<void>;
}

interface RegisterConfirmProps {
  onCancel: () => void;
  onDone: () => void;
  registerData: RegisterSpaceCraft;
  onPrevious: () => void;
}

interface RegisterFormProps {
  onCancel: () => void;
  setRegisterData: React.Dispatch<React.SetStateAction<RegisterSpaceCraft | null>>;
  initialData: RegisterSpaceCraft | null;
}

interface RegisterConfirmCardProps {
  title: string | number;
  label: string;
}

const convertToFormData = (data: RegisterSpaceCraft | null): Partial<FormData> => {
  if (!data) return {};

  return {
    name: data.name,
    noradId: data.noradId.toString(),
    tleTitle: data.tleTitle,
    tleFirst: data.tleFirst,
    tleSecond: data.tleSecond,
    framesize: data.framesize?.toString(),
    bitRate: data.bitRate?.toString(),
    centerFrequency: data.centerFrequency?.toString(),
    subcarrierFrequency: data.subcarrierFrequency?.toString(),
    polarization: data.polarization?.toString(),
    convolutionalCode: data.convolutionalCode?.toString(),
    reedSolomonCode: data.reedSolomonCode?.toString(),
    modulation: data.modulation?.toString(),
    pcmCoding: data.pcmCoding?.toString(),
    framesyncword: data.framesyncword,
    syn: data.syn,
    ctl: data.ctl,
    lts: data.lts,
    slip: data.slip,
    other: data.other,
    // Uplink 전용 필드
    pcmCode: data.pcmCode,
    idlePattern: data.idlePattern, // 16진수 문자열 그대로
    idlePatternLength: data.idlePatternLength?.toString(),
    preambleLength: data.preambleLength?.toString(),
    carrierFrequencyMhz: data.carrierFrequencyMhz?.toString(),
    operatingMode: data.operatingMode,
    ifCarrierLevel: data.ifCarrierLevel?.toString(),
    sweepRate: data.sweepRate?.toString(),
    sweepRange: data.sweepRange?.toString(),
  };
};

const convertToRegisterSpaceCraft = (data: FormData,selectedMenuIndex: number, selectedButtonIndex: number ): RegisterSpaceCraft => {
  const isUplink = selectedMenuIndex === TAB_MENU_INDEX.UPLINK;

  const parseNumber = (value: string | number | undefined): number | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const num = parseFloat(value.replace(/,/g, ''));
      return isNaN(num) ? undefined : num;
    }
    return undefined;
  };

  // FormData는 문자열 기반이므로 명시적으로 변환
  const baseData: RegisterSpaceCraft = {
    name: data.name,
    noradId: data.noradId,
    tleTitle: data.tleTitle,
    tleFirst: data.tleFirst,
    tleSecond: data.tleSecond,
    framesize: data.framesize, 
    bitRate: data.bitRate, 
    centerFrequency: data.centerFrequency, 
    subcarrierFrequency: data.subcarrierFrequency, 
    polarization: Number(data.polarization), 
    convolutionalCode: Number(data.convolutionalCode),
    reedSolomonCode: Number(data.reedSolomonCode),
    modulation: Number(data.modulation),
    pcmCoding: data.pcmCoding, 
    framesyncword: data.framesyncword,
    syn: data.syn,
    ctl: data.ctl,
    lts: data.lts,
    slip: data.slip,
    other: data.other,
    band: getBandString(selectedButtonIndex),
    direction: getDirectionString(selectedMenuIndex),
    directionName: getDirectionString(selectedMenuIndex),
    polarizationName: registerPolarizationList.find(item => item.value === Number(data.polarization))?.label || '',
    convolutionalCodeName:registerConvolutionalCodeList.find(item => item.value === Number(data.convolutionalCode))?.label || '',
    reedSolomonCodeName:  reedSolomonCodeList.find(item => item.value === Number(data.reedSolomonCode))?.label || '',
    pcmCodingName: registerPCMCodingList.find(item => item.value === Number(data.pcmCoding))?.label || '',
    modulationName: registerModulationList.find(item => item.value === Number(data.modulation))?.label || ''
  };

  // Uplink 전용 필드 추가 (Uplink는 항상 S_BAND)
  if (isUplink) {
    return {
      ...baseData,
      band: 'S_BAND', // Uplink는 항상 S_BAND만 사용
      pcmCode: data.pcmCode,
      idlePattern: data.idlePattern, // 16진수 문자열 그대로
      idlePatternLength: parseNumber(data.idlePatternLength),
      preambleLength: parseNumber(data.preambleLength),
      carrierFrequencyMhz: parseNumber(data.carrierFrequencyMhz),
      operatingMode: data.operatingMode,
      ifCarrierLevel: parseNumber(data.ifCarrierLevel),
      sweepRate: parseNumber(data.sweepRate),
      sweepRange: parseNumber(data.sweepRange),
    };
  }

  return baseData;
};


const RegisterConfirmCard = ({ title, label, isLast }: RegisterConfirmCardProps & { isLast?: boolean }) => {
  const { t } = useTranslation();
  const isTle = label === 'manage.modal.tleLine1' || label === 'manage.modal.tleLine2' || '';
  const isConvolutionalCode = label ==='manage.modal.convCode';
  const isOther= label === 'manage.modal.other';
  // console.log('title',title);
  // console.log('label',label);
  return (
    <div className={`flex h-[120px] flex-col items-center justify-center gap-1 rounded-lg border border-border-primary ${isLast ? 'col-span-2' : ''}`}>
      <Text
        className={`${
          isTle || isConvolutionalCode || isOther
            ? 'px-2 text-center'
            : 'text-center md:text-lg'
        }`}
        weight="medium"
      >
        {title.toString() ? title.toString() : '-'}
      </Text>
      <Text size="body2" color="tertiary" weight="regular" className='text-center'>
        {t(label)}
      </Text>
    </div>
  );
};

const RegisterConfirm = ({ registerData, onPrevious, onDone }: RegisterConfirmProps) => {
  const { t } = useTranslation(); // 추가가
  console.log('registerData',registerData);
  if (!registerData) {
    return <div>No data available</div>;
  }

  const isUplink = registerData.direction === 'UPLINK';

  // UPLINK와 DOWNLINK에 따라 다른 필드 목록 사용
  const downlinkOrder: (keyof RegisterSpaceCraft)[] = [
    'name',
    'directionName',
    'band',
    'noradId',
    'tleTitle',
    'tleFirst',
    'tleSecond',
    'centerFrequency',
    'bitRate',
    'subcarrierFrequency',
    'framesyncword',
    'framesize',
    'polarizationName',
    'modulationName',
    'convolutionalCodeName',
    'reedSolomonCodeName',
    'pcmCoding',
    'syn',
    'ctl',
    'lts',
    'slip',
    'other',
  ];

  const uplinkOrder: (keyof RegisterSpaceCraft)[] = [
    'name',
    'directionName',
    'band',
    'noradId',
    'tleTitle',
    'tleFirst',
    'tleSecond',
    'bitRate',
    'polarizationName',
    'pcmCode',
    'idlePattern',
    'idlePatternLength',
    'preambleLength',
    'carrierFrequencyMhz',
    'operatingMode',
    'ifCarrierLevel',
    'sweepRate',
    'sweepRange',
    'other',
  ];

  const customOrder = isUplink ? uplinkOrder : downlinkOrder;

  const registerDataLabels = customOrder.filter(
    (label) => label in registerData && !['direction', 'reedSolomonCode', 'modulation', 'polarization', 'convolutionalCode', 'randomization', 'pcmCoding'].includes(label)
  );

  const isLastItem = (index: number) => index === registerDataLabels.length - 1 && registerDataLabels.length % 2 === 1;

  // UPLINK와 DOWNLINK에 따라 다른 라벨 목록 사용
  const registerSpaceCraftLabels = registerSpaceCraftList.reduce((acc, value) => {
    if (!['linkDirection', 'reedSolomonCode', 'modulation', 'polarization', 'convolutionalCode', 'randomization', 'pcmCoding', 'randomizationName','pcmCodingName'].includes(value.key)) {
      acc[value.key] = value.title;
    }
    return acc;
  }, {} as { [key: string]: string });

  // UPLINK 전용 필드 라벨 추가
  if (isUplink) {
    registerSpaceCraftLabels['pcmCode'] = 'PCM Code';
    registerSpaceCraftLabels['idlePattern'] = 'Idle Pattern';
    registerSpaceCraftLabels['idlePatternLength'] = 'Idle Pattern Length';
    registerSpaceCraftLabels['preambleLength'] = 'Preamble Length';
    registerSpaceCraftLabels['carrierFrequencyMhz'] = 'Carrier Frequency (MHz)';
    registerSpaceCraftLabels['operatingMode'] = 'Operating Mode';
    registerSpaceCraftLabels['ifCarrierLevel'] = 'IF Carrier Level (dB)';
    registerSpaceCraftLabels['sweepRate'] = 'Sweep Rate';
    registerSpaceCraftLabels['sweepRange'] = 'Sweep Range';
  }

  // 값 변환 함수 (숫자 값을 문자열 라벨로 변환)
  const getDisplayValue = (label: keyof RegisterSpaceCraft, value: any): string | number => {
    if (isUplink) {
      // PCM Code: 숫자 값을 문자열 라벨로 변환
      if (label === 'pcmCode' && value !== undefined && value !== null && value !== '') {
        const pcmCodeOption = registerUplinkPCMCodeList.find(
          item => item.value === Number(value)
        );
        if (pcmCodeOption) {
          return pcmCodeOption.label;
        }
      }
      // Operating Mode: 숫자 값을 문자열 라벨로 변환
      if (label === 'operatingMode' && value !== undefined && value !== null && value !== '') {
        const operatingModeOption = registerOperatingModeList.find(
          item => item.value === Number(value)
        );
        if (operatingModeOption) {
          return operatingModeOption.label;
        }
      }
    }
    return value ?? '-';
  };

  return (
    <>
      <div className="grid max-h-[70vh] grid-cols-2 gap-2 overflow-auto p-4 pt-0">
        {registerDataLabels.map((label, index) => (
          <RegisterConfirmCard
            key={`${label}_${index}`}
            title={getDisplayValue(label, registerData[label])}
            label={registerSpaceCraftLabels[label]}
            isLast={isLastItem(index)}
          />
        ))}
      </div>
      <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
      <Button className="h-9" text={t('manage.button.previous')} onClick={onPrevious} type="secondary" size="large" />
      <Button className="h-9" text={t('manage.button.done')} onClick={onDone} type="blue" size="large" />
      </div>
    </>
  );
};

const TAB_MENU_INDEX = {
  DOWNLINK: 0,
  UPLINK: 1
};

const BUTTON_MENU_INDEX = {
  SBAND: 0,
  XBAND: 1
};

// 숫자 인덱스를 문자열로 변환하는 함수들
const getDirectionString = (index: number): 'DOWNLINK' | 'UPLINK' => {
  return index === TAB_MENU_INDEX.DOWNLINK ? 'DOWNLINK' : 'UPLINK';
};

const getBandString = (index: number): 'S_BAND' | 'X_BAND' => {
  return index === BUTTON_MENU_INDEX.SBAND ? 'S_BAND' : 'X_BAND';
};

const RegisterForm = ({ onCancel, setRegisterData, initialData }: RegisterFormProps) => {
  const { t } = useTranslation(); // 추가
  const [selectedMenuIndex, setSelectedMenuIndex] = useState(TAB_MENU_INDEX.DOWNLINK);
  const [selectedButtonIndex, setSelectedButtonMenuIndex] = useState(BUTTON_MENU_INDEX.SBAND);
  const {
    formData,
    errors,
    setErrors,
    handleInputChange,
    setFormData
  } = useSpaceCraftForm(convertToFormData(initialData));

  const { isLoading, error, tleData, fetchTLEData } = useTLEData();

  const handleFetchTLE = useCallback(() => {
    if (formData.noradId) {
      fetchTLEData(formData.noradId);
    }
  }, [formData.noradId, fetchTLEData]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleFetchTLE();
      }
    },
    [handleFetchTLE]
  );

  React.useEffect(() => {
    if (tleData) {
      setFormData(prev => ({
        ...prev,
        tleTitle: tleData.tleTitle,
        tleFirst: tleData.tleFirst,
        tleSecond: tleData.tleSecond
      }));
      // TLE 데이터 설정 후 각 필드 검증하여 에러 메시지 제거
      if (tleData.tleTitle) {
        validateField('tleTitle', tleData.tleTitle, setErrors);
      }
      if (tleData.tleFirst) {
        validateField('tleFirst', tleData.tleFirst, setErrors);
      }
      if (tleData.tleSecond) {
        validateField('tleSecond', tleData.tleSecond, setErrors);
      }
    }
  }, [tleData, setFormData, setErrors]);

  function onClickNext() {
    // UPLINK일 때는 Uplink 필드만, DOWNLINK일 때는 Downlink 필드만 검증
    const isUplink = selectedMenuIndex === TAB_MENU_INDEX.UPLINK;
    
    // 공통 필드
    const commonFields: (keyof FormData)[] = ['name', 'noradId', 'tleTitle', 'tleFirst', 'tleSecond', 'bitRate', 'polarization'];
    
    // Downlink 전용 필드
    const downlinkFields: (keyof FormData)[] = ['centerFrequency', 'modulation', 'convolutionalCode', 'reedSolomonCode', 'subcarrierFrequency', 'pcmCoding', 'framesyncword', 'framesize', 'syn', 'ctl', 'lts', 'slip'];
    
    // Uplink 전용 필드
    const uplinkFields: (keyof FormData)[] = ['pcmCode', 'idlePattern', 'idlePatternLength', 'preambleLength', 'carrierFrequencyMhz', 'operatingMode', 'ifCarrierLevel', 'sweepRate', 'sweepRange'];
    
    // 검증할 필드 선택
    const fieldsToValidate = isUplink 
      ? [...commonFields, ...uplinkFields]
      : [...commonFields, ...downlinkFields];
    
    // 선택된 필드만 검증
    let isValid = true;
    fieldsToValidate.forEach((field) => {
      if (!validateField(field, formData[field], setErrors)) {
        isValid = false;
      }
    });
    
    console.log('formData', formData);
    if (isValid) {
      // UPLINK일 때는 항상 S-Band로 고정
      const buttonIndex = isUplink 
        ? BUTTON_MENU_INDEX.SBAND 
        : selectedButtonIndex;
      
      setRegisterData({
        ...convertToRegisterSpaceCraft(
          formData, 
          selectedMenuIndex,
          buttonIndex
        ),
      });
    } else {
      console.log("Form validation failed", errors);
    }
  }
  function onClickTab(index: number) {
    setSelectedMenuIndex(index);
    // Uplink로 변경 시 S-Band로 초기화
    if (index === TAB_MENU_INDEX.UPLINK) {
      setSelectedButtonMenuIndex(BUTTON_MENU_INDEX.SBAND);
    }
  }

  return (
    <>
      <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto overflow-x-hidden p-4 pt-0">
        <MenuTab
          menuList={[t('manage.tab.downlink'), t('manage.tab.uplink')]}
          selectedMenuIndex={selectedMenuIndex}
          onClickTab={onClickTab}
        />

        {/* Downlink 영역 */}
        <div
          className={`
            flex flex-col
            ${selectedMenuIndex === TAB_MENU_INDEX.UPLINK ? 'hidden' : ''}
          `}
        >
          <MenuButton
            menuList={[t('manage.tab.sband'), t('manage.tab.xband')]}
            selectedMenuIndex={selectedButtonIndex}
            onClickTab={setSelectedButtonMenuIndex}
          />
          {selectedButtonIndex === BUTTON_MENU_INDEX.SBAND && (
            <SDownLink
              formData={formData}
              onNext={(data: FormData) => setFormData(data)}
              errors={errors}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
              error={error}
              handleFetchTLE={handleFetchTLE}
              handleKeyDown={handleKeyDown}
            />
          )}
          {selectedButtonIndex === BUTTON_MENU_INDEX.XBAND && (
            <XDownLink
              formData={formData}
              onNext={(data: FormData) => setFormData(data)}
              errors={errors}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
              error={error}
              handleFetchTLE={handleFetchTLE}
              handleKeyDown={handleKeyDown}
            />
          )}
        </div>

        {/* Uplink 영역 */}
        {selectedMenuIndex === TAB_MENU_INDEX.UPLINK && (
          <div className="flex flex-col">
            <MenuButton
              menuList={[t('manage.tab.sband')]}
              selectedMenuIndex={BUTTON_MENU_INDEX.SBAND}
              onClickTab={() => {
                // uplink는 S-band만 지원
              }}
            />
            <SUpLink
              formData={formData}
              onNext={(data: FormData) => setFormData(data)}
              errors={errors}
              handleInputChange={handleInputChange}
              isLoading={isLoading}
              error={error}
              handleFetchTLE={handleFetchTLE}
              handleKeyDown={handleKeyDown}
            />
          </div>
        )}
      </div>

      <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
        <Button
          className="h-9"
          text={t('manage.button.cancel')}
          onClick={onCancel}
          type="secondary"
          size="large"
        />
        <Button
          className="h-9"
          text={t('manage.button.next')}
          onClick={onClickNext}
          type="blue"
          size="large"
        />
      </div>
    </>
  );
};

const RegisterModal = ({ isOpened, setIsOpened, onSpaceCraftUpdated }: ModalProps) => {
  const [messageArray, setMessageArray] = useState<Message[]>([]);
  const [messageArrayCount, setMessageArrayCount] = useState(0);
  const [registerSpaceCraftData, setRegisterSpaceCraftData] = useState<RegisterSpaceCraft | null>(null);
  const [isConfirmStep, setIsConfirmStep] = useState(false);
  const { t } = useTranslation();

  const parseNumber = (value: string | number | undefined): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return parseFloat(value.replace(/,/g, ''));
    return 0;
  };

  function onCancel() {
    setRegisterSpaceCraftData(null);
    setIsOpened(false);
    setIsConfirmStep(false);
  }

  const onPrevious = () => {
    setIsConfirmStep(false);
  };

  function onClickDeleteButton(index: number) {
    setMessageArray((prev) => prev.filter((message: any) => message.index !== index));
  }

  async function onDone() {
    if (!registerSpaceCraftData) return;

    let params: any;
    let endpoint: string;

    if (registerSpaceCraftData.direction === 'UPLINK') {
      // Uplink API 구조 (s-up)
      // polarization을 문자열로 변환
      const polarizationMap: { [key: number]: string } = {
        0: 'LHCP',
        1: 'RHCP',
        2: 'BOTH'
      };
      const polarizationString = polarizationMap[registerSpaceCraftData.polarization] || 'LHCP';

      // Uplink 선택 옵션(숫자)을 API 스펙 문자열로 매핑
      const pcmCodeLabel =
        registerSpaceCraftData.pcmCode !== undefined
          ? (
              registerUplinkPCMCodeList.find(
                i => i.value === Number(registerSpaceCraftData.pcmCode)
              )?.label || ''
            )
          : '';
      const pcmCodeString = pcmCodeLabel.replace(/_/g, '-');

        const operatingModeString =
          registerSpaceCraftData.operatingMode !== undefined
            ? (
                registerOperatingModeList.find(
                  i => i.value === Number(registerSpaceCraftData.operatingMode)
                )?.label || ''
              )
            : '';

      params = {
        noradId: registerSpaceCraftData.noradId,
        tleTitle: registerSpaceCraftData.tleTitle,
        tleFirst: registerSpaceCraftData.tleFirst,
        tleSecond: registerSpaceCraftData.tleSecond,
        linkList: [
          {
            name: registerSpaceCraftData.name,
            other: registerSpaceCraftData.other || '',
            polarization: polarizationString,
            bitRate: parseNumber(registerSpaceCraftData.bitRate),
            pcmCode: pcmCodeString,
            idlePattern: registerSpaceCraftData.idlePattern,
            idlePatternLength: parseNumber(registerSpaceCraftData.idlePatternLength),
            preambleLength: parseNumber(registerSpaceCraftData.preambleLength),
            carrierFrequencyMhz: parseNumber(registerSpaceCraftData.carrierFrequencyMhz),
            operatingMode: operatingModeString,
            ifCarrierLevel: parseNumber(registerSpaceCraftData.ifCarrierLevel),
            sweepRate: parseNumber(registerSpaceCraftData.sweepRate),
            sweepRange: parseNumber(registerSpaceCraftData.sweepRange),
          }
        ]
      };
      endpoint = '/api/spacecraft/register/s-up';
    } else {
      // Downlink API 구조 (s-down, x-down)
      params = {
        noradId: registerSpaceCraftData.noradId,
        tleTitle: registerSpaceCraftData.tleTitle,
        tleFirst: registerSpaceCraftData.tleFirst,
        tleSecond: registerSpaceCraftData.tleSecond,
        linkList: [
          {
            name: registerSpaceCraftData.name,
            direction: registerSpaceCraftData.direction,
            centerFrequency: parseNumber(registerSpaceCraftData.centerFrequency),
            band: registerSpaceCraftData.band,
            polarizationKey: registerSpaceCraftData.polarization,
            modulationKey: registerSpaceCraftData.modulation,
            convolutionalCodeKey: registerSpaceCraftData.convolutionalCode,
            reedSolomonCodeKey: registerSpaceCraftData.reedSolomonCode,
            pcmCodingKey: registerSpaceCraftData.pcmCoding,
            subFrequency: parseNumber(registerSpaceCraftData.subcarrierFrequency),
            bitRate: parseNumber(registerSpaceCraftData.bitRate),
            frameSyncWord: registerSpaceCraftData.framesyncword,
            frameSize: parseNumber(registerSpaceCraftData.framesize),
            syn: registerSpaceCraftData.syn,
            ctl: registerSpaceCraftData.ctl,
            lts: registerSpaceCraftData.lts,
            slip: registerSpaceCraftData.slip,
            other: registerSpaceCraftData.other,
          }
        ]
      };
      endpoint = registerSpaceCraftData.band === 'S_BAND' 
        ? '/api/spacecraft/register/s-down'
        : '/api/spacecraft/register/x-down';
    }

    console.log('API Endpoint:', endpoint);
    console.log('API Request Params:', JSON.stringify(params, null, 2));

    try {
      const spacecraftRes = await axios.post(endpoint, params);
      onSpaceCraftUpdated();
      setMessageArray((prev) => [
        ...prev,
        {
          index: messageArrayCount,
          icon: <InformationCircleOutlineIcon />,
          text: `${registerSpaceCraftData.direction === 'UPLINK' ? 'Uplink' : 'Downlink'} ${registerSpaceCraftData.band === 'S_BAND' ? 'S-Band' : 'X-Band'} spacecraft registration has been completed`
        }
      ]);
      setTimeout(() => onClickDeleteButton(messageArrayCount), 2000);
      setMessageArrayCount((prev) => prev + 1);

      setRegisterSpaceCraftData(null);
      setIsOpened(false);
    } catch (error) {
      console.error('Error registering spacecraft:', error);
      setMessageArray((prev) => [
        ...prev,
        {
          index: messageArrayCount,
          icon: <InformationCircleOutlineIcon />,
          text: "Error registering spacecraft. Please try again."
        }
      ]);
      setTimeout(() => onClickDeleteButton(messageArrayCount), 2000);
      setMessageArrayCount((prev) => prev + 1);
      setRegisterSpaceCraftData(null);
    }
  }

  return (
    <>
      <Modal isOpened={isOpened}>
        <ModalInner title={t('manage.modal.title')} onClickClose={onCancel}>
          {isConfirmStep && registerSpaceCraftData ? (
            <RegisterConfirm 
              onCancel={onCancel} 
              onDone={onDone} 
              registerData={registerSpaceCraftData} 
              onPrevious={onPrevious}
            />
          ) : (
            <RegisterForm 
              onCancel={onCancel} 
              setRegisterData={(data) => {
                setRegisterSpaceCraftData(data);
                setIsConfirmStep(true);  
              }}
              initialData={registerSpaceCraftData} 
            />
          )}
        </ModalInner>
      </Modal>
      <InfoMessage messageArray={messageArray} onClickDelete={onClickDeleteButton} />
    </>
  );
};

export default RegisterModal;